import {
  faArrowUpRightFromSquare,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  FlexBox,
  JoonUIColor,
  SPACING,
  Slider,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useShallow } from "zustand/react/shallow"

import CatchUpCardEmptyState from "./CatchUpCardEmptyState"
import TimeOfDayCard from "./TimeOfDayCard"
import { useCatchUpStore } from "./useCatchUpStore"
import Counter from "../../components/counter/Counter"
import { useImageModalStore } from "../../components/modals/ImageModal"

const CatchUpCardContent = () => {
  const {
    selectedQuest,
    questReviewDifficulty,
    setQuestReviewDifficulty,
    questReviewReward,
    setQuestReviewReward,
    questReviewNotes,
    setQuestReviewNotes,
    selectedChild,
  } = useCatchUpStore(
    useShallow((state) => ({
      selectedQuest: state.selectedQuest,
      questReviewDifficulty: state.questReviewDifficulty,
      setQuestReviewDifficulty: state.setQuestReviewDifficulty,
      questReviewReward: state.questReviewReward,
      setQuestReviewReward: state.setQuestReviewReward,
      questReviewNotes: state.questReviewNotes,
      setQuestReviewNotes: state.setQuestReviewNotes,
      selectedChild: state.selectedChild,
    }))
  )
  const { t } = useTranslation()

  if (!selectedQuest) return <CatchUpCardEmptyState />

  const completionDate = dayjs.unix(Number(selectedQuest.completion_date))
  const completionText = t("CATCHUP_COMPLETED_AT", {
    date: completionDate?.format("MMMM D, YYYY"),
    time: completionDate?.format("h:mm A"),
  })

  return (
    <div>
      <div className="flex flex-col gap-2 py-2 px-3 border-b border-borderPrimary">
        <TimeOfDayCard
          date={selectedQuest.date}
          time={selectedQuest.series.reminder_time}
          title={selectedQuest.series.title}
          routine={selectedQuest.series.routine}
          isSelfInitiated={selectedQuest.series.assigner_profile === null}
          completionText={completionText}
        />
        {selectedQuest.series.photo_auto_verify_max &&
        selectedQuest.photo_auto_verify_count &&
        selectedQuest.photo_auto_verify_count > 0 ? (
          <div className="flex flex-col p-3 border border-red-500 rounded-md bg-red-100">
            <div className="flex flex-row justify-between w-full">
              <div className="flex flex-row gap-2 flex-1 items-center">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  color={JoonUIColor.semantic.alert}
                />
                <Typography
                  variant="bodySmallBold"
                  textAlign="left"
                  color={JoonUIColor.semantic.alert}
                >
                  {t("CATCHUP_REJECTED_BY_AI_COUNT", {
                    count: selectedQuest.photo_auto_verify_count,
                  })}
                </Typography>
              </div>

              <button
                className="flex flex-row gap-2 items-center"
                onClick={() =>
                  window.open(
                    "https://docs.google.com/forms/d/e/1FAIpQLScqxqKS5VZmwY1KTSbeyvuHPx1SXKnCJci94Ea8H3rGVArxvg/viewform",
                    "_blank"
                  )
                }
              >
                <Typography
                  variant="bodySmall"
                  textAlign="left"
                  className="whitespace-nowrap"
                  color={JoonUIColor.icon.accent}
                >
                  {t("CATCHUP_SEND_FEEDBACK")}
                </Typography>
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  color={JoonUIColor.icon.accent}
                  fontSize={10}
                />
              </button>
            </div>
            <Typography
              variant="bodySmall"
              textAlign="left"
              color={JoonUIColor.semantic.alert}
            >
              "{selectedQuest.verification_notes}"
            </Typography>
          </div>
        ) : null}
      </div>
      <CompletionNotes />
      <div
        style={{
          zIndex: 1001,
          borderTopLeftRadius: SPACING.space4,
          borderTopRightRadius: SPACING.space4,
          background: "white",
          padding: `${SPACING.space4} ${SPACING.space6}`,
        }}
      >
        <Typography
          variant="bodySmallBold"
          textAlign="center"
          style={{ marginBottom: SPACING.space1 }}
        >
          {t("CATCHUP_DIFFICULTY_QUESTION", {
            name: selectedChild?.user.name,
          })}
        </Typography>
        <Slider
          value={questReviewDifficulty}
          options={[0, 1, 2, 3, 4, 5, 6]}
          setValue={setQuestReviewDifficulty}
          minLabel={t("CATCHUP_VERY_EASY")}
          maxLabel={t("CATCHUP_VERY_DIFFICULT")}
        />
      </div>
      <div style={{ padding: `${SPACING.space4} ${SPACING.space6}` }}>
        <Typography variant="bodySmallBold" textAlign="center">
          {t("CATCHUP_REWARD_ADJUSTMENT")}
        </Typography>
        <Typography
          variant="bodySmall"
          textAlign="center"
          color={JoonUIColor.text.secondary}
          style={{ marginBottom: SPACING.space1 }}
        >
          {t("CATCHUP_REWARD_ADJUSTMENT_DESC")}
        </Typography>
        <Counter value={questReviewReward} setValue={setQuestReviewReward} />
      </div>
      <FlexBox
        direction="column"
        gap={SPACING.space1}
        style={{ padding: `${SPACING.space4} ${SPACING.space6}` }}
      >
        <Typography variant="bodySmallBold" textAlign="center">
          {t("CATCHUP_ADD_NOTE", { name: selectedChild?.user.name })}
        </Typography>
        <TextInput
          name="Note"
          placeholder={t("CATCHUP_NOTE_PLACEHOLDER")}
          value={questReviewNotes}
          onChange={(e) => setQuestReviewNotes(e.target.value)}
          fullWidth
          maxLength={255}
        />
      </FlexBox>
    </div>
  )
}

const CompletionNotes = () => {
  const { onOpen } = useImageModalStore()
  const { t } = useTranslation()
  const { selectedQuest } = useCatchUpStore(
    useShallow((state) => ({
      selectedQuest: state.selectedQuest,
      selectedChild: state.selectedChild,
    }))
  )
  if (!selectedQuest) return null

  const hasDataToShow =
    selectedQuest.completion_photo_url ||
    selectedQuest.completion_notes ||
    selectedQuest.series.description

  if (!hasDataToShow) return null

  return (
    <div className="bg-bgXLightGray">
      {selectedQuest.completion_photo_url && (
        <button
          onClick={() =>
            selectedQuest.completion_photo_url &&
            onOpen(selectedQuest.completion_photo_url)
          }
        >
          <img
            src={selectedQuest.completion_photo_url || ""}
            alt="Quest completion verification"
            className="w-full h-full object-cover"
          />
        </button>
      )}
      {selectedQuest.completion_notes && (
        <div className="flex flex-col px-4 py-3">
          <Typography variant="bodySmallBold" textAlign="left">
            {t("CATCHUP_COMPLETION_NOTE")}
          </Typography>
          <Typography variant="bodySmall" textAlign="left">
            "{selectedQuest.completion_notes}"
          </Typography>
        </div>
      )}
      {selectedQuest.series.description && (
        <div className="flex flex-col px-4 py-3">
          <Typography variant="bodySmallBold" textAlign="left">
            {t("CATCHUP_QUEST_DESCRIPTION")}
          </Typography>
          <Typography variant="bodySmall" textAlign="left">
            {selectedQuest.series.description}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default CatchUpCardContent
